import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReviewerNavbar from '../components/ReviewerNavbar';
import WatermarkedPlayer from '../components/WatermarkedPlayer';
import { ReviewerAPI, useReviewerAuth } from '../context/ReviewerAuthContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { Star, ThumbsUp, ThumbsDown, Minus, Save, Eye, EyeOff, AlertCircle, CheckCircle, Lock } from 'lucide-react';

// "Screenplay" and "Engaging" were merged into one category (2026-08) —
// score_screenplay is the surviving column; score_engaging is retired
// (kept in the DB for old rows, no longer shown or written to here).
// "Editing" is new.
const SCORE_FIELDS = [
  { key: 'score_cinematography', label: 'Cinematography', desc: 'Visual composition, lighting, camera work, shot selection' },
  { key: 'score_screenplay', label: 'Screenplay/Engaging', desc: 'Story structure, dialogue, narrative flow, character arcs, and how captivating the film is overall' },
  { key: 'score_acting', label: 'Acting', desc: 'Performance quality, emotional authenticity, character portrayal' },
  { key: 'score_direction', label: 'Direction', desc: 'Creative vision, pacing, scene staging, tonal consistency' },
  { key: 'score_dialogue', label: 'Dialogue', desc: 'Naturalism, subtext, cultural authenticity, delivery' },
  { key: 'score_music', label: 'Music', desc: 'Score, sound design, and how well the audio supports the film' },
  { key: 'score_editing', label: 'Editing', desc: 'Pacing of cuts, transitions, and how well the footage is assembled' },
];

function ScoreSlider({ field, value, onChange, disabled }) {
  const pct = ((value || 0) / 10) * 100;
  const color = value >= 7 ? 'var(--green)' : value >= 4 ? 'var(--amber)' : value > 0 ? 'var(--red)' : 'var(--border)';

  return (
    <div style={{ marginBottom: 20, opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 1 }}>{field.label}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{field.desc}</div>
        </div>
        <div style={{ fontWeight: 900, fontSize: 22, color, minWidth: 44, textAlign: 'right' }}>
          {value > 0 ? value : '—'}
        </div>
      </div>
      <div style={{ position: 'relative', height: 6, background: 'var(--bg-elevated)', borderRadius: 3, marginBottom: 4 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, var(--pink), ${color})`, borderRadius: 3, transition: 'width 0.2s' }} />
      </div>
      <input type="range" min={0} max={10} step={0.5} value={value || 0} disabled={disabled}
        onChange={e => onChange(parseFloat(e.target.value))} style={{ width: '100%' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>
        <span>0 — Poor</span><span>5 — Average</span><span>10 — Excellent</span>
      </div>
    </div>
  );
}

export default function ReviewerReview() {
  const { reviewer } = useReviewerAuth();
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('scores');
  const [showPassword, setShowPassword] = useState(false);
  // Which of the 3 written-feedback boxes an admin has marked required in
  // Settings — default all-optional until the assignment loads.
  const [feedbackRequirements, setFeedbackRequirements] = useState({ strengths: false, improvements: false, summary: false });

  const [scores, setScores] = useState({
    score_cinematography: 0, score_screenplay: 0, score_acting: 0,
    score_editing: 0, score_direction: 0, score_dialogue: 0, score_music: 0,
    score_other: 0, score_other_label: '',
  });
  const [feedback, setFeedback] = useState({
    feedback_strengths: '', feedback_improvements: '',
    feedback_summary: '', recommendation: '',
  });

  useEffect(() => {
    // Uses ReviewerAPI (separate instance — fixes the "not showing" bug)
    ReviewerAPI.get(`/api/reviewer/assignment/${assignmentId}`)
      .then(r => {
        const a = r.data.assignment;
        setAssignment(a);
        if (r.data.feedback_requirements) setFeedbackRequirements(r.data.feedback_requirements);
        if (a.score_cinematography !== null && a.score_cinematography !== undefined) {
          setScores({
            score_cinematography: parseFloat(a.score_cinematography) || 0,
            score_screenplay: parseFloat(a.score_screenplay) || 0,
            score_acting: parseFloat(a.score_acting) || 0,
            score_editing: parseFloat(a.score_editing) || 0,
            score_direction: parseFloat(a.score_direction) || 0,
            score_dialogue: parseFloat(a.score_dialogue) || 0,
            score_music: parseFloat(a.score_music) || 0,
            score_other: parseFloat(a.score_other) || 0,
            score_other_label: a.score_other_label || '',
          });
          setFeedback({
            feedback_strengths: a.feedback_strengths || '',
            feedback_improvements: a.feedback_improvements || '',
            feedback_summary: a.feedback_summary || '',
            recommendation: a.recommendation || '',
          });
        }
      })
      .catch(() => navigate('/reviewer/dashboard'))
      .finally(() => setLoading(false));
  }, [assignmentId, navigate]);

  const setScore = (key, val) => setScores(s => ({ ...s, [key]: val }));
  const setFeedbackField = (key, val) => setFeedback(f => ({ ...f, [key]: val }));

  const scoredFields = SCORE_FIELDS.map(f => scores[f.key]).filter(v => v > 0);
  const overall = scoredFields.length > 0
    ? (scoredFields.reduce((a, b) => a + b, 0) / scoredFields.length).toFixed(1) : null;
  const locked = assignment?.assignment_status === 'completed';

  // Which written-feedback boxes are still missing, given admin-configured
  // requirements (Settings → per-box checkboxes, default: none required).
  const missingFeedback = [
    feedbackRequirements.strengths && !feedback.feedback_strengths.trim() && 'What works well',
    feedbackRequirements.improvements && !feedback.feedback_improvements.trim() && 'Areas to improve',
    feedbackRequirements.summary && !feedback.feedback_summary.trim() && 'Summary note',
  ].filter(Boolean);
  const isComplete = scoredFields.length >= 6 && feedback.recommendation && missingFeedback.length === 0;

  const handleSave = async (asFinal = false) => {
    if (asFinal) {
      const ok = await confirm("Once submitted, you can't edit this review yourself — you'll need to ask an admin to reopen it. Submit anyway?", { title: 'Submit review?', confirmText: 'Submit' });
      if (!ok) return;
    }
    setSaving(true);
    try {
      await ReviewerAPI.post(`/api/reviewer/assignment/${assignmentId}/score`, {
        ...scores, ...feedback, final: asFinal,
      });
      setAssignment(a => ({ ...a, status: asFinal ? 'completed' : 'in_progress' }));
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (asFinal) navigate('/reviewer/dashboard');
      }, 1500);
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: 'var(--pink)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!assignment) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <ReviewerNavbar />
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '36px 24px' }}>

        {/* Film header */}
        <div className="card" style={{ padding: 24, marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="badge badge-blue">Blind Review</span>
            {assignment.genre && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{assignment.genre}</span>}
            {assignment.language && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{assignment.language}</span>}
            {assignment.duration && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{assignment.duration}</span>}
          </div>
          <h1 style={{ fontWeight: 900, fontSize: 26, color: 'var(--text)', marginBottom: 12, letterSpacing: '-0.01em' }}>{assignment.title}</h1>
          {assignment.synopsis && <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14, fontWeight: 400, borderLeft: '3px solid var(--pink)', paddingLeft: 14 }}>{assignment.synopsis}</p>}
          {assignment.notes_to_reviewers && (
            <div style={{ background: 'rgba(59,130,246,0.06)', border: '1.5px solid rgba(59,130,246,0.2)', borderRadius: 'var(--r-md)', padding: '11px 14px', marginBottom: 14, display: 'flex', gap: 8 }}>
              <Eye size={13} color="var(--blue)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--blue)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Note from Filmmaker</div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400, lineHeight: 1.6 }}>{assignment.notes_to_reviewers}</p>
              </div>
            </div>
          )}
          {assignment.admin_notes_for_reviewers && (
            <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.2)', borderRadius: 'var(--r-md)', padding: '11px 14px', marginBottom: 14, display: 'flex', gap: 8 }}>
              <Eye size={13} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--amber)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Note from Admin</div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400, lineHeight: 1.6 }}>{assignment.admin_notes_for_reviewers}</p>
              </div>
            </div>
          )}
          {assignment.film_link && (
            <div style={{ marginBottom: 12 }}>
              <WatermarkedPlayer link={assignment.film_link} reviewer={reviewer} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {assignment.film_link_password && (
              <button onClick={() => setShowPassword(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4, fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)', padding: '9px 14px', cursor: 'pointer', fontFamily: 'var(--font)' }}>
                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                {showPassword
                  ? <span>Password: <span style={{ fontWeight: 800, color: 'var(--text)', fontFamily: 'monospace' }}>{assignment.film_link_password}</span></span>
                  : <span>Reveal link password</span>}
              </button>
            )}
            {assignment.trailer_link && <a href={assignment.trailer_link} target="_blank" rel="noopener noreferrer" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)', padding: '9px 18px', fontSize: 12, fontWeight: 700, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.04em' }}>▶ Trailer</a>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
            <AlertCircle size={11} color="var(--text-muted)" />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Filmmaker identity is hidden. Your review remains anonymous. Film access is logged per your confidentiality agreement — do not share this link or password.</span>
          </div>
        </div>

        {/* Section tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {[['scores','📊 Scores'],['feedback','💬 Feedback']].map(([val, label]) => (
            <button key={val} onClick={() => setActiveSection(val)}
              style={{ padding: '9px 20px', borderRadius: 'var(--r-md)', fontSize: 12, fontWeight: 700, border: `1.5px solid ${activeSection === val ? 'var(--pink)' : 'var(--border)'}`, background: activeSection === val ? 'var(--pink-glow)' : 'var(--bg-elevated)', color: activeSection === val ? 'var(--pink)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </button>
          ))}
          {overall && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)', padding: '8px 16px' }}>
              <Star size={14} color="var(--pink)" fill="var(--pink)" />
              <span style={{ fontWeight: 900, fontSize: 20, color: 'var(--pink)' }}>{overall}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Overall</span>
            </div>
          )}
        </div>

        {/* Scores */}
        {activeSection === 'scores' && (
          <div className="card" style={{ padding: 28 }}>
            <div className="section-label" style={{ marginBottom: 22 }}>Film Scores (0–10)</div>
            {SCORE_FIELDS.map(field => <ScoreSlider key={field.key} field={field} value={scores[field.key]} onChange={v => setScore(field.key, v)} disabled={locked} />)}
            <div style={{ borderTop: '1.5px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Custom Category (optional)</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input value={scores.score_other_label} onChange={e => setScore('score_other_label', e.target.value)} disabled={locked} placeholder="e.g. VFX, Editing, Costume Design" style={{ flex: 1, fontSize: 13, padding: '9px 12px' }} />
                <div style={{ flex: 2 }}><ScoreSlider field={{ key:'score_other', label:'', desc:'' }} value={scores.score_other} onChange={v => setScore('score_other', v)} disabled={locked} /></div>
              </div>
            </div>
          </div>
        )}

        {/* Feedback */}
        {activeSection === 'feedback' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="card" style={{ padding: 22 }}>
              <div className="section-label" style={{ marginBottom: 6 }}>Your Recommendation</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, fontWeight: 400 }}>Advisory only — final decision is made by admin.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                {[['recommend','var(--green)',<ThumbsUp size={14}/>,'Recommend','Strong film'],['consider','var(--amber)',<Minus size={14}/>,'Consider','Has potential'],['pass','var(--red)',<ThumbsDown size={14}/>,'Pass','Not ready']].map(([val,color,icon,label,desc]) => (
                  <button key={val} onClick={() => !locked && setFeedbackField('recommendation', val)} disabled={locked}
                    style={{ flex:1, padding:'12px 8px', borderRadius:'var(--r-md)', border:`1.5px solid ${feedback.recommendation===val?color:'var(--border)'}`, background:feedback.recommendation===val?`${color}15`:'var(--bg-elevated)', color:feedback.recommendation===val?color:'var(--text-muted)', cursor:locked?'default':'pointer', opacity:locked?0.6:1, fontFamily:'var(--font)', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    {icon}
                    <span style={{ fontWeight:800, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</span>
                    <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:400 }}>{desc}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="card" style={{ padding: 22 }}>
              <div className="section-label" style={{ marginBottom: 6 }}>Written Feedback</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18, fontWeight: 400 }}>Anonymous feedback compiled for the filmmaker. Be honest and constructive.</p>
              {[['feedback_strengths','✓ What works well','What did the film do well? Be specific.',3,'strengths'],['feedback_improvements','△ Areas to improve','What could be stronger? Suggest solutions.',3,'improvements'],['feedback_summary','◎ Summary note','2–3 sentence overall assessment for the filmmaker.',4,'summary']].map(([key,label,hint,rows,reqKey]) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:800, color:'var(--text-secondary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                    {label} {feedbackRequirements[reqKey] ? <span style={{ color:'var(--pink)' }}>*</span> : <span style={{ color:'var(--text-muted)', fontWeight:500, textTransform:'none', letterSpacing:0 }}>(optional)</span>}
                  </label>
                  <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:6, fontWeight:400 }}>{hint}</p>
                  <textarea value={feedback[key]} onChange={e => setFeedbackField(key, e.target.value)} disabled={locked} rows={rows} placeholder="Write your feedback..." style={{ fontSize:13, resize:'vertical', lineHeight:1.7 }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status + Actions */}
        {locked ? (
          <>
            <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginTop:18, padding:'12px 16px', background:'var(--bg-elevated)', border:'1.5px solid var(--border)', borderRadius:'var(--r-md)' }}>
              <Lock size={14} color="var(--text-muted)" style={{ flexShrink:0, marginTop:1 }} />
              <span style={{ fontSize:12, color:'var(--text-secondary)', fontWeight:600, lineHeight:1.6 }}>
                You've already submitted this review. Contact an admin if you need to make a change.
              </span>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:12 }}>
              <button onClick={() => navigate('/reviewer/dashboard')} className="btn-ghost" style={{ flex:1 }}>Back</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:18, padding:'11px 14px', background:isComplete?'rgba(0,200,150,0.06)':'var(--bg-elevated)', border:`1.5px solid ${isComplete?'rgba(0,200,150,0.2)':'var(--border)'}`, borderRadius:'var(--r-md)' }}>
              {isComplete ? <CheckCircle size={14} color="var(--green)"/> : <AlertCircle size={14} color="var(--text-muted)"/>}
              <span style={{ fontSize:12, color:isComplete?'var(--green)':'var(--text-muted)', fontWeight:600 }}>
                {isComplete ? 'Review complete — ready to submit' : `Need: ${[
                  scoredFields.length < 6 ? `${6-scoredFields.length} more score(s)` : '',
                  !feedback.recommendation ? 'recommendation' : '',
                  ...missingFeedback,
                ].filter(Boolean).join(', ')}`}
              </span>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:12 }}>
              <button onClick={() => navigate('/reviewer/dashboard')} className="btn-ghost" style={{ flex:1 }}>Back</button>
              <button onClick={() => handleSave(false)} disabled={saving} className="btn-ghost" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, color:'var(--blue)', borderColor:'rgba(59,130,246,0.3)' }}>
                <Save size={13}/> Save Draft
              </button>
              <button onClick={() => handleSave(true)} disabled={saving||!isComplete} className="btn-primary" style={{ flex:2, padding:'11px', fontSize:12 }}>
                {saved?'✓ Submitted!':saving?'Submitting...':'Submit Review →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
